// EXTERNAL DEPENDENCIES
import React, { useState } from 'react'

// HELPERS
import {
  invokeOnSpaceOrEnter,
  parseAndTrackClick,
  usePrefixedContent,
  usePrefixedMessage,
} from '@/utils'
import { getPayButtonFpti } from './utils'

// COMPONENTS
import {
  BodyText,
  Button,
  CaptionText,
  Card,
  Container,
  HeadingText,
  Link,
  PaypalAppIcon,
  Row,
  ShareIcon,
  ShoppingCartIcon,
} from '@paypalcorp/pp-react'
import ProfilePhotoDisplay from '../profile-photo-display'
import CoverPhotoDisplay from '../cover-photo-display'
import { WithLoadingShimmer } from '@/common/components/loading/with-loading-shimmer'
import ShareProfileSheetContainer from '../../share-profile-sheet-container'
import SectionHeader from '../../section-header'
import ContactInfoSection from './components/contact-info-section'

// CONSTANTS
import { REPORT_PROFILE_URL } from '@/constants'
import routes from '@/api/constants/routes'
import {
  PROFILE_CARD_BUSINESS_DESCRIPTION_TEST_ID,
  PROFILE_CARD_BUSINESS_HANDLE_NAME_TEST_ID,
  PROFILE_CARD_BUSINESS_NAME_HEADER_TEST_ID,
  PROFILE_CARD_CONTAINER_TEST_ID,
  PROFILE_CARD_PAYPAL_JOIN_DATE_TEST_ID,
  PROFILE_PAY_BUTTON_TEST_ID,
} from '@/constants/test-ids'

// STYLES
import { mainBrandPrimaryBlueText } from '@/styles/colors'
import styles from './styles.module.less'

// TYPES
import { ProfileCardProps } from './types'

// CONSTANTS
export const profileCardContentPrefix = 'components.ui.profile.profile-card.'

/**
 * Displays a user's profile card in either buyer or seller mode
 * that can or cannot be interacted with depending on the props passed
 */
export default function ProfileCard({
  userOwnsProfile,
  isLoading,
  isReviewing,
  isPreviewing,
  CardFooterButtons,
  fpti,
  data: {
    accountCreateDate,
    businessAddress,
    businessDescription,
    businessHandle,
    businessName,
    contactInfo,
    coverPhotoSrc,
    pastYearCustomerCount = 0,
    profilePhotoSrc,
    reviewHeaderText,
    reviewSubheaderText,
    selectedLocationDisplayPreference,
  },
}: ProfileCardProps) {
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)
  const interactionsDisabled = isReviewing || isPreviewing

  // INITIALIZE CONTENT
  const getContent = usePrefixedContent(profileCardContentPrefix)
  const getMessage = usePrefixedMessage(profileCardContentPrefix)

  const shareLinkText = getContent('share')
  const payButtonText = getContent('pay')
  const businessDescriptionHeaderText = getContent('businessDescriptionHeader')
  const reportProfileLinkText = getContent('reportProfileLink')
  const paypalJoinDateText = getContent('joinedPayPal', {
    accountCreateDate,
  })

  // Disclaimer link text
  const reviewDisclaimerText = getMessage('reviewDisclaimer', {
    link: (msg: string) => (
      <Link
        href='https://www.paypal.com/us/legalhub/pp-biz-profile-tnc'
        target='_blank'
        data-fpti={disclaimerLinkFpti}
      >
        {msg}
      </Link>
    ),
  })
  // Past year customer count text
  const customersInLastYearSingleText = getContent('customersInLastYearSingle')
  const customersInLastYeaMultipleText = getContent(
    'customersInLastYeaMultiple',
    { pastYearCustomerCount },
  )
  const pastYearCustomerCountText =
    pastYearCustomerCount === 1
      ? customersInLastYearSingleText
      : customersInLastYeaMultipleText

  // FPTI
  const { disclaimerLinkFpti, eventName, flowData, shareLinkFpti } = fpti || {}

  const payButtonFpti = getPayButtonFpti(eventName)

  const reportLink = (
    <Row className={styles.reportLink}>
      <Link
        target='_blank'
        href={REPORT_PROFILE_URL}
        isLoading={isLoading}
        disabled={interactionsDisabled}
        tabIndex={interactionsDisabled ? -1 : 0}
      >
        {reportProfileLinkText}
      </Link>
    </Row>
  )

  const handleShareClick = () => {
    setIsShareSheetOpen(true)
    parseAndTrackClick(shareLinkFpti)
  }

  return (
    <Container data-testid={PROFILE_CARD_CONTAINER_TEST_ID} xxl form fluid>
      {isReviewing && reviewHeaderText ? (
        <SectionHeader
          headerText={reviewHeaderText}
          subheaderText={reviewSubheaderText}
        />
      ) : null}
      <Card className={styles.profileCardContainer}>
        <div className={interactionsDisabled && styles.disableInteractions}>
          <CoverPhotoDisplay
            isDisplayPhoto
            src={coverPhotoSrc}
            isLoading={isLoading}
          />

          <Card.Content
            className={
              styles[
                isPreviewing
                  ? 'reviewCardContentWithCtaButtons'
                  : 'reviewCardContentContainer'
              ]
            }
          >
            <div className={styles.profilePhotoContainer}>
              <ProfilePhotoDisplay src={profilePhotoSrc} isLoading={isLoading}>
                {isLoading ? null : (
                  <Link
                    isLoading={isLoading}
                    disabled={interactionsDisabled}
                    tabIndex={interactionsDisabled ? -1 : 0}
                    leftIcon={
                      <ShareIcon
                        aria-hidden
                        size='sm'
                        className={styles.shareIcon}
                      />
                    }
                    className={styles.shareLink}
                    onClick={handleShareClick}
                    onKeyDown={invokeOnSpaceOrEnter(handleShareClick)}
                  >
                    {shareLinkText}
                  </Link>
                )}
              </ProfilePhotoDisplay>
            </div>
            <div className={styles.businessInfoContainer}>
              <Row noGutters className={styles.businessSectionContainer}>
                <HeadingText
                  size='sm'
                  isLoading={isLoading}
                  data-testid={PROFILE_CARD_BUSINESS_NAME_HEADER_TEST_ID}
                >
                  {businessName}
                </HeadingText>
                <CaptionText
                  isLoading={isLoading}
                  data-testid={PROFILE_CARD_BUSINESS_HANDLE_NAME_TEST_ID}
                >{`@${businessHandle}`}</CaptionText>
              </Row>
              {userOwnsProfile && !isPreviewing && !isReviewing ? null : (
                <WithLoadingShimmer isLoading={isLoading}>
                  <Row noGutters align='start'>
                    <Button
                      href={`${routes.PAYMENT_TRANSFER_HOMEPAGE}?from=business_profile&flowContextData=${flowData}`}
                      className={styles.payButton}
                      data-testid={PROFILE_PAY_BUTTON_TEST_ID}
                      data-fpti={payButtonFpti}
                      as={interactionsDisabled ? 'button' : 'a'} // is otherwise clickable to screen readers
                      tabIndex={interactionsDisabled ? -1 : 0}
                    >
                      {payButtonText}
                    </Button>
                  </Row>
                </WithLoadingShimmer>
              )}
              <Row noGutters className={styles.sectionContainer}>
                {businessDescription ? (
                  <>
                    <Row noGutters>
                      <BodyText strong isLoading={isLoading}>
                        {businessDescriptionHeaderText}
                      </BodyText>
                    </Row>
                    <Row noGutters>
                      <BodyText
                        isLoading={isLoading}
                        data-testid={PROFILE_CARD_BUSINESS_DESCRIPTION_TEST_ID}
                        className={styles.profileDescriptionTextContainer}
                      >
                        {businessDescription}
                      </BodyText>
                    </Row>
                  </>
                ) : null}
                {accountCreateDate ? (
                  <WithLoadingShimmer isLoading={isLoading}>
                    <Row noGutters className={styles.iconSection}>
                      <PaypalAppIcon
                        aria-hidden
                        color={mainBrandPrimaryBlueText}
                        size='sm'
                      />
                      <BodyText
                        data-testid={PROFILE_CARD_PAYPAL_JOIN_DATE_TEST_ID}
                      >
                        {paypalJoinDateText}
                      </BodyText>
                    </Row>
                  </WithLoadingShimmer>
                ) : null}
                <WithLoadingShimmer isLoading={isLoading}>
                  <Row noGutters className={styles.iconSection}>
                    <ShoppingCartIcon
                      aria-hidden
                      color={mainBrandPrimaryBlueText}
                      size='sm'
                    />
                    <BodyText>{pastYearCustomerCountText}</BodyText>
                  </Row>
                </WithLoadingShimmer>
              </Row>
              <Row noGutters className={styles.sectionContainer}>
                <ContactInfoSection
                  businessAddress={businessAddress}
                  contactInfo={contactInfo}
                  isLoading={isLoading}
                  interactionsDisabled={interactionsDisabled}
                  selectedLocationDisplayPreference={
                    selectedLocationDisplayPreference
                  }
                />
              </Row>
            </div>
            {isPreviewing && reportLink}
          </Card.Content>
        </div>
        {CardFooterButtons && (
          <Card.Footer className={styles.footer}>
            <CardFooterButtons handleShareProfileClick={handleShareClick} />
          </Card.Footer>
        )}
      </Card>
      {isReviewing ? (
        <BodyText className={styles.reviewDisclaimer}>
          {reviewDisclaimerText}
        </BodyText>
      ) : null}
      {userOwnsProfile || isReviewing ? null : reportLink}
      <ShareProfileSheetContainer
        isBuyer={!userOwnsProfile}
        isShareSheetOpen={isShareSheetOpen}
        setIsShareSheetOpen={setIsShareSheetOpen}
      />
    </Container>
  )
}
